import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Handshake,
  Plus,
  Search,
  ExternalLink,
  Send,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Eye,
  CreditCard,
  Building2,
  FileText,
  DollarSign,
  TrendingUp,
  Receipt,
  RotateCcw,
  Sparkles,
  Share2,
  ShieldCheck,
  Check,
  Download,
  Copy,
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  Award,
  Upload,
  Image as ImageIcon,
  Trash2
} from 'lucide-react';
import { PaymentAgreement, AgreementStatus } from './PaymentAgreementTypes';
import { PaymentAgreementSimulator } from './PaymentAgreementSimulator';
import { DebtorPortalModal } from './DebtorPortalModal';
import { PaymentAgreementPdfService } from '@/services/paymentAgreementPdfService';
import { PhLogoStorageService } from '@/services/phLogoStorageService';
import {
  PaymentAgreementDatabaseService,
  AdminContactSettings,
  DEFAULT_ADMIN_CONTACT_SETTINGS
} from '@/services/paymentAgreementDatabaseService';
import { toast } from 'sonner';

interface DebtorOption {
  id?: string;
  copropiedad_nombre?: string;
  direccion?: string;
  unidad_privada: string;
  propietario_nombre: string;
  propietario_id?: string;
  telefono?: string;
  email?: string;
  saldo_capital: number;
  saldo_intereses: number;
  dias_mora?: number;
}

interface PaymentAgreementsTabProps {
  debtors: DebtorOption[];
  copropiedadNombre?: string;
  copropiedadNit?: string;
  administradorNombre?: string;
  onOpenChargeTabWithDebtor?: (debtor: DebtorOption) => void;
}

const STORAGE_KEY = 'legal_notifications_agreements_v1';

// Acuerdos de ejemplo realistas pre-cargados si no hay en localStorage
const DEFAULT_AGREEMENTS: PaymentAgreement[] = [
  {
    id: 'AGR-904123',
    copropiedad_id: 'default',
    copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
    copropiedad_nit: '900.852.147-3',
    administrador_nombre: 'Administración PH',
    administrador_telefono: '(601) 745-8900 Ext. 102',
    administrador_email: 'cartera@torresdelparque.com',
    administrador_whatsapp: '3184567890',
    administrador_horario: 'Lunes a Viernes 8:00 AM - 5:00 PM | Sábados 8:00 AM - 12:00 M',
    administrador_oficina: 'Oficina de Administración - Torre 1, Piso 1',
    unidad_privada: 'Torre 2 Apto 504',
    propietario_nombre: 'Carlos Eduardo Restrepo',
    propietario_id: '79845123',
    telefono: '3104567890',
    email: 'carlos.restrepo@gmail.com',

    deuda_total_inicial: 3200000,
    capital_original: 2600000,
    intereses_originales: 600000,
    porcentaje_condonacion_intereses: 50,
    intereses_condonados: 300000,
    deuda_pactada: 2900000,

    cuota_inicial_monto: 725000,
    cuota_inicial_porcentaje: 25,
    cuota_inicial_pagada: true,
    cuota_inicial_fecha_limite: '2026-10-15',
    cuota_inicial_referencia: 'PSE-894123',

    saldo_a_financiar: 2175000,
    numero_cuotas: 6,
    valor_cuota_mensual: 362500,
    tasa_financiacion_mensual: 0,
    dia_pago_mensual: 10,
    fecha_inicio: '2026-10-10',

    estado: 'al_dia',
    cuotas_pagadas: 2,
    cuotas_totales: 6,
    total_recaudado: 1450000, // inicial + 2 cuotas
    saldo_restante: 1450000,
    proximo_vencimiento: '2026-11-10',

    magic_token: 'tok_carlos_restrepo_t2_504',
    portal_url: `${window.location.origin}/portal-deudor/torres-del-parque/tok_carlos_restrepo_t2_504`,
    firmado_digitalmente_at: '2026-10-11T14:30:00Z',

    cuotas_detalle: [
      { numero: 1, fecha_vencimiento: '2026-10-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pagada', fecha_pago: '2026-10-10', referencia_pago: 'PSE-109283' },
      { numero: 2, fecha_vencimiento: '2026-11-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pagada', fecha_pago: '2026-10-24', referencia_pago: 'NEQUI-88129' },
      { numero: 3, fecha_vencimiento: '2026-12-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' },
      { numero: 4, fecha_vencimiento: '2027-01-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' },
      { numero: 5, fecha_vencimiento: '2027-02-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' },
      { numero: 6, fecha_vencimiento: '2027-03-10', capital: 362500, interes: 0, total_cuota: 362500, estado: 'pendiente' }
    ],
    created_at: '2026-10-10T10:00:00Z',
    updated_at: '2026-10-24T18:00:00Z'
  },
  {
    id: 'AGR-881942',
    copropiedad_id: 'default',
    copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
    copropiedad_nit: '900.852.147-3',
    administrador_nombre: 'Administración PH',
    administrador_telefono: '(601) 745-8900 Ext. 102',
    administrador_email: 'cartera@torresdelparque.com',
    administrador_whatsapp: '3184567890',
    administrador_horario: 'Lunes a Viernes 8:00 AM - 5:00 PM | Sábados 8:00 AM - 12:00 M',
    administrador_oficina: 'Oficina de Administración - Torre 1, Piso 1',
    unidad_privada: 'Torre 1 Apto 302',
    propietario_nombre: 'Marcela Gómez Duarte',
    propietario_id: '52891456',
    telefono: '3157891234',
    email: 'marcela.gomez@hotmail.com',

    deuda_total_inicial: 1850000,
    capital_original: 1500000,
    intereses_originales: 350000,
    porcentaje_condonacion_intereses: 70,
    intereses_condonados: 245000,
    deuda_pactada: 1605000,

    cuota_inicial_monto: 400000,
    cuota_inicial_porcentaje: 25,
    cuota_inicial_pagada: true,
    cuota_inicial_fecha_limite: '2026-09-20',
    cuota_inicial_referencia: 'BANCOL-4821',

    saldo_a_financiar: 1205000,
    numero_cuotas: 4,
    valor_cuota_mensual: 301250,
    tasa_financiacion_mensual: 0,
    dia_pago_mensual: 15,
    fecha_inicio: '2026-09-15',

    estado: 'en_mora',
    cuotas_pagadas: 1,
    cuotas_totales: 4,
    total_recaudado: 701250,
    saldo_restante: 903750,
    proximo_vencimiento: '2026-10-15', // Vencida
    mora_dias: 10,

    magic_token: 'tok_marcela_gomez_t1_302',
    portal_url: `${window.location.origin}/portal-deudor/torres-del-parque/tok_marcela_gomez_t1_302`,

    cuotas_detalle: [
      { numero: 1, fecha_vencimiento: '2026-09-15', capital: 301250, interes: 0, total_cuota: 301250, estado: 'pagada', fecha_pago: '2026-09-15', referencia_pago: 'PSE-39201' },
      { numero: 2, fecha_vencimiento: '2026-10-15', capital: 301250, interes: 0, total_cuota: 301250, estado: 'vencida' },
      { numero: 3, fecha_vencimiento: '2026-11-15', capital: 301250, interes: 0, total_cuota: 301250, estado: 'pendiente' },
      { numero: 4, fecha_vencimiento: '2026-12-15', capital: 301250, interes: 0, total_cuota: 301250, estado: 'pendiente' }
    ],
    created_at: '2026-09-15T09:00:00Z',
    updated_at: '2026-10-16T08:00:00Z'
  },
  {
    id: 'AGR-772105',
    copropiedad_id: 'default',
    copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
    copropiedad_nit: '900.852.147-3',
    administrador_nombre: 'Administración PH',
    administrador_telefono: '(601) 745-8900 Ext. 102',
    administrador_email: 'cartera@torresdelparque.com',
    administrador_whatsapp: '3184567890',
    administrador_horario: 'Lunes a Viernes 8:00 AM - 5:00 PM | Sábados 8:00 AM - 12:00 M',
    administrador_oficina: 'Oficina de Administración - Torre 1, Piso 1',
    unidad_privada: 'Torre 3 Apto 102',
    propietario_nombre: 'Diana Patricia Salazar',
    propietario_id: '43920184',
    telefono: '3119876543',
    email: 'diana.salazar@outlook.com',

    deuda_total_inicial: 2400000,
    capital_original: 2000000,
    intereses_originales: 400000,
    porcentaje_condonacion_intereses: 100,
    intereses_condonados: 400000,
    deuda_pactada: 2000000,

    cuota_inicial_monto: 500000,
    cuota_inicial_porcentaje: 25,
    cuota_inicial_pagada: true,
    cuota_inicial_fecha_limite: '2026-07-15',
    cuota_inicial_referencia: 'PSE-109482',

    saldo_a_financiar: 1500000,
    numero_cuotas: 3,
    valor_cuota_mensual: 500000,
    tasa_financiacion_mensual: 0,
    dia_pago_mensual: 15,
    fecha_inicio: '2026-07-15',

    estado: 'cumplido',
    cuotas_pagadas: 3,
    cuotas_totales: 3,
    total_recaudado: 2000000,
    saldo_restante: 0,
    proximo_vencimiento: 'COMPLETADO',

    magic_token: 'tok_diana_salazar_t3_102',
    portal_url: `${window.location.origin}/portal-deudor/torres-del-parque/tok_diana_salazar_t3_102`,
    firmado_digitalmente_at: '2026-07-15T09:20:00Z',

    cuotas_detalle: [
      { numero: 1, fecha_vencimiento: '2026-08-15', capital: 500000, interes: 0, total_cuota: 500000, estado: 'pagada', fecha_pago: '2026-08-14', referencia_pago: 'PSE-552011' },
      { numero: 2, fecha_vencimiento: '2026-09-15', capital: 500000, interes: 0, total_cuota: 500000, estado: 'pagada', fecha_pago: '2026-09-15', referencia_pago: 'NEQUI-99412' },
      { numero: 3, fecha_vencimiento: '2026-10-15', capital: 500000, interes: 0, total_cuota: 500000, estado: 'pagada', fecha_pago: '2026-10-14', referencia_pago: 'BANCOL-7721' }
    ],
    created_at: '2026-07-15T09:00:00Z',
    updated_at: '2026-10-14T16:00:00Z'
  }
];

export const PaymentAgreementsTab: React.FC<PaymentAgreementsTabProps> = ({
  debtors,
  copropiedadNombre = 'Conjunto Residencial Torres del Parque',
  copropiedadNit = '900.852.147-3',
  administradorNombre = 'Administración PH',
  onOpenChargeTabWithDebtor
}) => {
  const [agreements, setAgreements] = useState<PaymentAgreement[]>([]);
  const [activeSubView, setActiveSubView] = useState<'list' | 'simulator'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('todos');

  // Modales
  const [portalModalAgreement, setPortalModalAgreement] = useState<PaymentAgreement | null>(null);
  const [isPortalModalOpen, setIsPortalModalOpen] = useState(false);

  // Modal para registrar abono manual
  const [paymentModalAgreement, setPaymentModalAgreement] = useState<PaymentAgreement | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentRefInput, setPaymentRefInput] = useState('');
  const [paymentAmountInput, setPaymentAmountInput] = useState<number>(0);

  // Configuración de canales de administración ingresados por el administrador
  const [adminContactSettings, setAdminContactSettings] = useState<AdminContactSettings>(() => {
    const s = PaymentAgreementDatabaseService.getAdminContactSettings();
    return {
      ...s,
      copropiedad_nombre: copropiedadNombre || s.copropiedad_nombre,
      copropiedad_nit: copropiedadNit || s.copropiedad_nit,
      administrador_nombre: administradorNombre || s.administrador_nombre
    };
  });
  const [isAdminContactModalOpen, setIsAdminContactModalOpen] = useState(false);
  const [isSavingAdminContact, setIsSavingAdminContact] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoFileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploadingLogo(true);
    try {
      const res = await PhLogoStorageService.uploadLogo(
        file,
        adminContactSettings.copropiedad_nombre,
        adminContactSettings.copropiedad_nit
      );

      setAdminContactSettings(prev => ({
        ...prev,
        copropiedad_logo_url: res.publicUrl,
        copropiedad_logo_data: res.dataUrl
      }));

      toast.success(`Logo subido exitosamente a Supabase (ph-logos/${res.folder}/)`);
    } catch (err: any) {
      console.error('Error al subir logo:', err);
      toast.error(err.message || 'No se pudo subir el logo a Supabase Storage.');
    } finally {
      setIsUploadingLogo(false);
      if (logoFileInputRef.current) {
        logoFileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveLogo = () => {
    setAdminContactSettings(prev => ({
      ...prev,
      copropiedad_logo_url: '',
      copropiedad_logo_data: ''
    }));
    toast.info('Logo institucional removido de la configuración.');
  };

  const handleSaveAdminContactSettings = async () => {
    setIsSavingAdminContact(true);
    try {
      await PaymentAgreementDatabaseService.saveAdminContactSettings(adminContactSettings);
      const updated = await PaymentAgreementDatabaseService.getAllAgreements();
      if (updated && updated.length > 0) {
        setAgreements(updated);
      }
      toast.success('Datos de administración actualizados y sincronizados.');
      setIsAdminContactModalOpen(false);
    } catch (err) {
      toast.error('Error al guardar datos de administración.');
    } finally {
      setIsSavingAdminContact(false);
    }
  };

  // Cargar acuerdos de la base de datos Supabase / localStorage
  useEffect(() => {
    let isMounted = true;
    const loadAgreements = async () => {
      try {
        const list = await PaymentAgreementDatabaseService.getAllAgreements();
        if (isMounted) {
          if (list && list.length > 0) {
            setAgreements(list);
          } else {
            setAgreements(DEFAULT_AGREEMENTS);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_AGREEMENTS));
            DEFAULT_AGREEMENTS.forEach((a) => PaymentAgreementDatabaseService.persistAgreement(a));
          }
        }
      } catch (err) {
        console.warn('Error loading agreements from database:', err);
        if (isMounted) setAgreements(DEFAULT_AGREEMENTS);
      }
    };
    loadAgreements();
    return () => {
      isMounted = false;
    };
  }, []);

  const saveAgreements = (updated: PaymentAgreement[]) => {
    setAgreements(updated);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      updated.forEach((a) => {
        PaymentAgreementDatabaseService.persistAgreement(a, {
          agreement_id: a.id,
          event: 'status_changed',
          actor_type: 'admin',
          metadata: { estado: a.estado, total_recaudado: a.total_recaudado }
        });
      });
    } catch (err) {
      console.warn('Error saving agreements to database:', err);
    }
  };

  // KPIs
  const stats = useMemo(() => {
    const total = agreements.length;
    const activos = agreements.filter(a => a.estado === 'al_dia' || a.estado === 'firmado_activo').length;
    const enMora = agreements.filter(a => a.estado === 'en_mora').length;
    const cumplidos = agreements.filter(a => a.estado === 'cumplido').length;

    const carteraPactada = agreements.reduce((acc, a) => acc + (a.deuda_pactada || 0), 0);
    const totalRecaudado = agreements.reduce((acc, a) => acc + (a.total_recaudado || 0), 0);
    const pctRecaudo = carteraPactada > 0 ? ((totalRecaudado / carteraPactada) * 100).toFixed(1) : '0';

    return {
      total,
      activos,
      enMora,
      cumplidos,
      carteraPactada,
      totalRecaudado,
      pctRecaudo
    };
  }, [agreements]);

  // Filtrado de acuerdos
  const filteredAgreements = useMemo(() => {
    return agreements.filter(a => {
      const matchesSearch =
        a.unidad_privada.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.propietario_nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (a.copropiedad_nombre && a.copropiedad_nombre.toLowerCase().includes(searchQuery.toLowerCase())) ||
        a.propietario_id.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matchesSearch) return false;
      if (statusFilter === 'todos') return true;
      if (statusFilter === 'al_dia') return a.estado === 'al_dia' || a.estado === 'firmado_activo';
      if (statusFilter === 'en_mora') return a.estado === 'en_mora';
      if (statusFilter === 'cumplido') return a.estado === 'cumplido';
      return a.estado === statusFilter;
    });
  }, [agreements, searchQuery, statusFilter]);

  // Manejar creación de un nuevo acuerdo desde el simulador
  const handleSaveNewAgreement = (newAgreement: PaymentAgreement) => {
    const updated = [newAgreement, ...agreements];
    saveAgreements(updated);
    setActiveSubView('list');
    toast.success(`Acuerdo registrado para ${newAgreement.unidad_privada}`);
  };

  // Abrir portal modal preview
  const handleOpenPortal = (agreement: PaymentAgreement) => {
    setPortalModalAgreement(agreement);
    setIsPortalModalOpen(true);
  };

  // Compartir por WhatsApp
  const handleShareWhatsApp = (agreement: PaymentAgreement) => {
    PaymentAgreementPdfService.shareAgreementViaWhatsApp(agreement);
  };

  // Abrir modal de registrar abono
  const handleOpenPaymentModal = (agreement: PaymentAgreement) => {
    setPaymentModalAgreement(agreement);
    const nextAmount = !agreement.cuota_inicial_pagada
      ? agreement.cuota_inicial_monto
      : agreement.valor_cuota_mensual;
    setPaymentAmountInput(nextAmount);
    setPaymentRefInput(`TRANS-${Math.floor(100000 + Math.random() * 900000)}`);
    setIsPaymentModalOpen(true);
  };

  // Confirmar registro de abono
  const handleConfirmInstallmentPayment = () => {
    if (!paymentModalAgreement) return;

    const agreement = paymentModalAgreement;
    let cuotasPagadas = agreement.cuotas_pagadas;
    let cuotaInicialPagada = agreement.cuota_inicial_pagada;
    const cuotasDetalle = [...agreement.cuotas_detalle];

    if (!cuotaInicialPagada) {
      cuotaInicialPagada = true;
    } else {
      // Buscar primera cuota pendiente o vencida
      const idx = cuotasDetalle.findIndex(c => c.estado === 'pendiente' || c.estado === 'vencida');
      if (idx !== -1) {
        cuotasDetalle[idx] = {
          ...cuotasDetalle[idx],
          estado: 'pagada',
          fecha_pago: new Date().toISOString().split('T')[0],
          referencia_pago: paymentRefInput
        };
        cuotasPagadas += 1;
      }
    }

    const nuevoRecaudado = agreement.total_recaudado + paymentAmountInput;
    const nuevoSaldo = Math.max(0, agreement.deuda_pactada - nuevoRecaudado);
    const nuevoEstado: AgreementStatus = nuevoSaldo <= 0
      ? 'cumplido'
      : 'al_dia';

    // Próximo vencimiento
    const nextPending = cuotasDetalle.find(c => c.estado === 'pendiente');
    const proximoVenc = nextPending ? nextPending.fecha_vencimiento : 'COMPLETADO';

    const updatedAgreement: PaymentAgreement = {
      ...agreement,
      cuota_inicial_pagada: cuotaInicialPagada,
      cuotas_pagadas: cuotasPagadas,
      total_recaudado: nuevoRecaudado,
      saldo_restante: nuevoSaldo,
      estado: nuevoEstado,
      proximo_vencimiento: proximoVenc,
      cuotas_detalle: cuotasDetalle,
      updated_at: new Date().toISOString()
    };

    const updatedList = agreements.map(a => a.id === updatedAgreement.id ? updatedAgreement : a);
    saveAgreements(updatedList);
    setIsPaymentModalOpen(false);

    if (nuevoEstado === 'cumplido') {
      toast.success(
        `🎉 ¡Acuerdo cancelado al 100%! La unidad ${agreement.unidad_privada} se encuentra a Paz y Salvo.`,
        {
          duration: 9000,
          action: {
            label: 'Descargar Paz y Salvo',
            onClick: () => PaymentAgreementPdfService.downloadPazYSalvoPdf(updatedAgreement)
          }
        }
      );
    } else {
      toast.success(`Abono por $${paymentAmountInput.toLocaleString('es-CO')} registrado con éxito para ${agreement.unidad_privada}`);
    }
  };

  const getStatusBadge = (estado: AgreementStatus) => {
    switch (estado) {
      case 'al_dia':
        return <Badge className="bg-emerald-600 text-white text-[10px]">Al Día</Badge>;
      case 'firmado_activo':
        return <Badge className="bg-sky-600 text-white text-[10px]">Firmado Activo</Badge>;
      case 'en_mora':
        return <Badge className="bg-rose-600 text-white text-[10px] animate-pulse">En Mora</Badge>;
      case 'cumplido':
        return (
          <Badge className="bg-emerald-700 hover:bg-emerald-800 text-white text-[10px] font-bold gap-1 shadow-sm">
            <Award className="h-3 w-3" /> A Paz y Salvo
          </Badge>
        );
      case 'enviado_al_deudor':
        return <Badge variant="outline" className="border-amber-400 text-amber-700 text-[10px]">Enviado al Deudor</Badge>;
      default:
        return <Badge variant="outline" className="text-[10px]">{estado}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* ── BARRA SUPERIOR DE LA PESTAÑA: KPIS Y SWITCH DE VISTA ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold flex items-center gap-2 text-slate-900 dark:text-white">
            <Handshake className="h-5 w-5 text-emerald-600" />
            <span>Módulo de Acuerdos de Pago &amp; Portal de Autogestión</span>
            <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-300 text-[11px]">
              Mérito Ejecutivo Art. 2469 C.C.
            </Badge>
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Simulador de cuotas, condonación de intereses, actas formales y enlaces de pago en línea para copropietarios.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {adminContactSettings.copropiedad_logo_data || adminContactSettings.copropiedad_logo_url ? (
            <div className="h-8 w-8 rounded-lg bg-white border border-slate-200 dark:border-slate-800 p-0.5 flex items-center justify-center shrink-0 shadow-xs overflow-hidden" title="Logo Oficial de la Copropiedad">
              <img
                src={adminContactSettings.copropiedad_logo_data || adminContactSettings.copropiedad_logo_url}
                alt="Logo PH"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : null}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsAdminContactModalOpen(true)}
            className="border-blue-300 text-blue-800 dark:border-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 text-xs h-8 gap-1.5 font-medium shadow-xs"
            title="Configurar canales de contacto y logo institucional para acuerdos y paz y salvos"
          >
            <Building2 className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
            ⚙️ Datos &amp; Logo PH
          </Button>

          {activeSubView === 'simulator' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActiveSubView('list')}
              className="text-xs h-8 gap-1.5"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Volver al Listado de Acuerdos
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setActiveSubView('simulator')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 gap-1.5 font-semibold shadow-sm"
            >
              <Plus className="h-3.5 w-3.5" />
              Nuevo Acuerdo (Simulador)
            </Button>
          )}
        </div>
      </div>

      {/* ── TARJETAS DE MÉTRICAS (KPIS DE RECAUDO POR ACUERDOS) ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Acuerdos Activos
              </span>
              <span className="text-2xl font-black text-slate-900 dark:text-white">
                {stats.activos}
              </span>
              <span className="text-[10px] text-muted-foreground block mt-0.5">
                De {stats.total} formalizados
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
              <Handshake className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Cartera Comprometida
              </span>
              <span className="text-xl font-bold text-slate-900 dark:text-white">
                ${(stats.carteraPactada / 1000000).toFixed(1)}M
              </span>
              <span className="text-[10px] text-emerald-600 block mt-0.5">
                ${stats.carteraPactada.toLocaleString('es-CO')} COP
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
              <DollarSign className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                Recaudado Efectivo
              </span>
              <span className="text-xl font-black text-emerald-600">
                ${(stats.totalRecaudado / 1000000).toFixed(1)}M
              </span>
              <span className="text-[10px] text-emerald-700 dark:text-emerald-300 font-semibold block mt-0.5">
                {stats.pctRecaudo}% de la meta
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
              <TrendingUp className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border shadow-xs">
          <CardContent className="p-3.5 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                En Mora / Alerta
              </span>
              <span className="text-2xl font-black text-rose-600">
                {stats.enMora}
              </span>
              <span className="text-[10px] text-rose-600 block mt-0.5 font-medium">
                {stats.enMora > 0 ? 'Exige cobro o demanda' : 'Sin alertas de mora'}
              </span>
            </div>
            <div className="p-2.5 rounded-xl bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400">
              <AlertTriangle className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── CUERPO PRINCIPAL: SIMULADOR O LISTADO DE ACUERDOS ── */}
      {activeSubView === 'simulator' ? (
        <PaymentAgreementSimulator
          debtors={debtors}
          copropiedadNombre={adminContactSettings.copropiedad_nombre || copropiedadNombre}
          copropiedadNit={adminContactSettings.copropiedad_nit || copropiedadNit}
          administradorNombre={adminContactSettings.administrador_nombre || administradorNombre}
          administradorTelefono={adminContactSettings.administrador_telefono}
          administradorEmail={adminContactSettings.administrador_email}
          administradorWhatsapp={adminContactSettings.administrador_whatsapp}
          administradorHorario={adminContactSettings.administrador_horario}
          administradorOficina={adminContactSettings.administrador_oficina}
          onSaveAgreement={handleSaveNewAgreement}
          onOpenPortalPreview={handleOpenPortal}
        />
      ) : (
        <Card className="border shadow-sm">
          <CardHeader className="pb-3 border-b bg-muted/10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por Torre/Apto o Copropietario..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8 text-xs h-8"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground font-medium">Filtrar:</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-xs h-8 rounded-md border border-input bg-background px-2.5 shadow-sm"
                >
                  <option value="todos">Todos los estados</option>
                  <option value="al_dia">Al Día / Vigentes</option>
                  <option value="en_mora">En Mora de Cuota</option>
                  <option value="cumplido">Cumplidos / Finiquitados</option>
                </select>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {filteredAgreements.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground space-y-2">
                <Handshake className="h-8 w-8 mx-auto text-muted-foreground/40" />
                <p className="text-sm font-medium">No se encontraron acuerdos con los filtros aplicados.</p>
                <Button
                  size="sm"
                  onClick={() => setActiveSubView('simulator')}
                  className="text-xs h-8 mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Crear Primer Acuerdo de Pago
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="text-xs bg-muted/30">
                      <TableHead>PH / Conjunto</TableHead>
                      <TableHead>Inmueble</TableHead>
                      <TableHead>Copropietario</TableHead>
                      <TableHead>Deuda Pactada</TableHead>
                      <TableHead className="w-48">Progreso de Cuotas</TableHead>
                      <TableHead>Próximo Vencimiento</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead className="text-right">Acciones Rápidas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAgreements.map((agreement) => {
                      const pct = agreement.cuotas_totales > 0
                        ? Math.round((agreement.cuotas_pagadas / agreement.cuotas_totales) * 100)
                        : 0;

                      return (
                        <TableRow key={agreement.id} className="text-xs hover:bg-muted/30">
                          <TableCell className="font-medium text-foreground whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                              <span>{agreement.copropiedad_nombre || copropiedadNombre || 'Copropiedad'}</span>
                            </div>
                          </TableCell>

                          <TableCell className="font-bold text-foreground whitespace-nowrap">
                            {agreement.unidad_privada}
                          </TableCell>

                          <TableCell>
                            <div className="space-y-0.5">
                              <span className="font-semibold block">{agreement.propietario_nombre}</span>
                              <span className="text-[10px] text-muted-foreground font-mono">
                                C.C. {agreement.propietario_id} &bull; {agreement.telefono}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div>
                              <span className="font-bold text-emerald-700 dark:text-emerald-300 block">
                                ${agreement.deuda_pactada.toLocaleString('es-CO')}
                              </span>
                              <span className="text-[10px] text-muted-foreground line-through">
                                Inicial: ${agreement.deuda_total_inicial.toLocaleString('es-CO')}
                              </span>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div className="space-y-1.5">
                              <div className="flex items-center justify-between text-[11px]">
                                <span className="font-medium">
                                  {agreement.cuotas_pagadas} de {agreement.cuotas_totales} cuotas
                                </span>
                                <span className="font-bold text-emerald-600">{pct}%</span>
                              </div>
                              <Progress value={pct} className="h-1.5 bg-slate-200 dark:bg-slate-800" />
                              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                                <span>Inicial: {agreement.cuota_inicial_pagada ? '✅ Pagada' : '⏳ Pendiente'}</span>
                                <span>Recaudado: ${(agreement.total_recaudado / 1000).toFixed(0)}k</span>
                              </div>
                            </div>
                          </TableCell>

                          <TableCell>
                            <div>
                              <span className={`font-semibold block ${agreement.estado === 'en_mora' ? 'text-rose-600 font-bold' : ''}`}>
                                {agreement.proximo_vencimiento}
                              </span>
                              {agreement.estado === 'en_mora' ? (
                                <Badge variant="outline" className="text-[9px] border-rose-300 text-rose-700 bg-rose-50 dark:bg-rose-950">
                                  {agreement.mora_dias || 5} días en mora
                                </Badge>
                              ) : (
                                <span className="text-[10px] text-slate-500">
                                  Cuota: ${agreement.valor_cuota_mensual.toLocaleString('es-CO')}
                                </span>
                              )}
                            </div>
                          </TableCell>

                          <TableCell>
                            {getStatusBadge(agreement.estado)}
                          </TableCell>

                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {agreement.estado === 'cumplido' || agreement.saldo_restante <= 0 ? (
                                <>
                                  <Button
                                    size="sm"
                                    onClick={() => PaymentAgreementPdfService.downloadPazYSalvoPdf(agreement)}
                                    className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm"
                                    title="Descargar Certificado Oficial de Paz y Salvo (PDF)"
                                  >
                                    <Award className="h-3.5 w-3.5" /> Paz y Salvo
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => PaymentAgreementPdfService.sharePazYSalvoViaWhatsApp(agreement)}
                                    className="h-7 text-xs gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950 font-medium"
                                    title="Enviar Paz y Salvo por WhatsApp al Propietario"
                                  >
                                    <Send className="h-3 w-3" /> WA
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => PaymentAgreementPdfService.downloadAgreementPdf(agreement)}
                                    className="h-7 text-xs gap-1 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-muted font-medium"
                                    title="Descargar Contrato Original del Acuerdo de Pago"
                                  >
                                    <Download className="h-3 w-3" /> Contrato
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenPortal(agreement)}
                                    className="h-7 text-xs gap-1 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-muted"
                                    title="Abrir Portal del Deudor"
                                  >
                                    <Eye className="h-3 w-3" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => PaymentAgreementPdfService.downloadAgreementPdf(agreement)}
                                    className="h-7 text-xs gap-1 border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950 font-semibold"
                                    title="Descargar Acuerdo de Pago en PDF Oficial"
                                  >
                                    <Download className="h-3 w-3" /> PDF
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleOpenPortal(agreement)}
                                    className="h-7 text-xs gap-1 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-muted"
                                    title="Abrir Portal de Autogestión del Deudor"
                                  >
                                    <Eye className="h-3 w-3" /> Portal
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => PaymentAgreementPdfService.copyPortalLink(agreement)}
                                    className="h-7 text-xs gap-1 border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-muted"
                                    title="Copiar Enlace del Portal del Deudor"
                                  >
                                    <Copy className="h-3 w-3" />
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleShareWhatsApp(agreement)}
                                    className="h-7 text-xs gap-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                                    title="Enviar Resumen y Enlace por WhatsApp"
                                  >
                                    <Send className="h-3 w-3" />
                                  </Button>

                                  <Button
                                    size="sm"
                                    onClick={() => handleOpenPaymentModal(agreement)}
                                    className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                                    title="Registrar Pago / Abono de Cuota"
                                  >
                                    <CreditCard className="h-3 w-3" /> Abonar
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── MODAL DEL PORTAL DEL DEUDOR (SIMULADOR DE EXPERIENCIA MÓVIL) ── */}
      <DebtorPortalModal
        isOpen={isPortalModalOpen}
        onClose={() => setIsPortalModalOpen(false)}
        agreement={portalModalAgreement}
        onPaymentSuccess={(agreementId, cuotaNum, refPago) => {
          // Actualizar estado del acuerdo cuando el deudor paga en el portal
          const updated = agreements.map(a => {
            if (a.id === agreementId) {
              const inicial = true;
              const cuotasPagadas = Math.min(a.cuotas_totales, a.cuotas_pagadas + 1);
              const totalRec = a.total_recaudado + (a.cuota_inicial_pagada ? a.valor_cuota_mensual : a.cuota_inicial_monto);
              return {
                ...a,
                cuota_inicial_pagada: inicial,
                cuotas_pagadas: cuotasPagadas,
                total_recaudado: totalRec,
                estado: (totalRec >= a.deuda_pactada) ? 'cumplido' as AgreementStatus : 'al_dia' as AgreementStatus,
                updated_at: new Date().toISOString()
              };
            }
            return a;
          });
          saveAgreements(updated);
        }}
      />

      {/* ── MODAL PARA REGISTRAR ABONO MANUAL DE CUOTA (ADMIN) ── */}
      <Dialog open={isPaymentModalOpen} onOpenChange={setIsPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Receipt className="h-5 w-5 text-emerald-600" />
              <span>Registrar Abono o Cuota de Acuerdo</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              {paymentModalAgreement?.unidad_privada} &bull; {paymentModalAgreement?.propietario_nombre}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 py-2 text-xs">
            <div className="p-3 rounded-lg border bg-emerald-50/50 dark:bg-emerald-950/20 text-emerald-900 dark:text-emerald-200 flex justify-between items-center">
              <span>Concepto sugerido:</span>
              <strong className="font-bold">
                {!paymentModalAgreement?.cuota_inicial_pagada
                  ? 'Cuota Inicial'
                  : `Cuota #${(paymentModalAgreement?.cuotas_pagadas || 0) + 1} de ${paymentModalAgreement?.cuotas_totales}`}
              </strong>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Valor Recibido ($ COP) *</Label>
              <Input
                type="number"
                value={paymentAmountInput}
                onChange={(e) => setPaymentAmountInput(parseFloat(e.target.value) || 0)}
                className="text-xs h-9 font-bold font-mono"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Referencia / Comprobante Bancario *</Label>
              <Input
                placeholder="Ej: BANCOL-984124 o TRANS-12345"
                value={paymentRefInput}
                onChange={(e) => setPaymentRefInput(e.target.value)}
                className="text-xs h-9 font-mono"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsPaymentModalOpen(false)} className="text-xs h-8">
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmInstallmentPayment}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 font-semibold"
            >
              Confirmar Abono
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── MODAL: CONFIGURACIÓN DE DATOS DE CONTACTO DE ADMINISTRACIÓN ── */}
      <Dialog open={isAdminContactModalOpen} onOpenChange={setIsAdminContactModalOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-slate-900 dark:text-white">
                  Datos de Contacto de Administración
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Estos canales oficiales se muestran al deudor en su portal cuando solicita contactar a la administración o aclarar dudas de su acuerdo.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-3.5 py-2 text-xs max-h-[65vh] overflow-y-auto pr-1">
            {/* Copropiedad y NIT */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Nombre de la Copropiedad / PH *</Label>
                <Input
                  value={adminContactSettings.copropiedad_nombre}
                  onChange={(e) => setAdminContactSettings({ ...adminContactSettings, copropiedad_nombre: e.target.value })}
                  placeholder="Ej: Conjunto Residencial Torres del Parque"
                  className="text-xs h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">NIT de la Copropiedad *</Label>
                <Input
                  value={adminContactSettings.copropiedad_nit}
                  onChange={(e) => setAdminContactSettings({ ...adminContactSettings, copropiedad_nit: e.target.value })}
                  placeholder="Ej: 900.852.147-3"
                  className="text-xs h-8 font-mono"
                />
              </div>
            </div>

            {/* Logo Institucional de la PH (Membrete de Documentos) */}
            <div className="p-3.5 rounded-xl border bg-slate-50/70 dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs font-bold flex items-center gap-1.5 text-slate-800 dark:text-slate-200">
                    <ImageIcon className="h-4 w-4 text-emerald-600" />
                    Logo Institucional de la Copropiedad
                  </Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Se incrusta automáticamente en el encabezado de los Acuerdos de Pago y Certificados de Paz y Salvo.
                  </p>
                </div>
                <Badge variant="outline" className="text-[10px] bg-white dark:bg-slate-800 border-emerald-300 text-emerald-700 font-mono">
                  Bucket: ph-logos
                </Badge>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-3.5">
                {/* Vista previa del logo */}
                <div className="w-24 h-20 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 flex items-center justify-center overflow-hidden p-1.5 shrink-0 shadow-inner">
                  {adminContactSettings.copropiedad_logo_data || adminContactSettings.copropiedad_logo_url ? (
                    <img
                      src={adminContactSettings.copropiedad_logo_data || adminContactSettings.copropiedad_logo_url}
                      alt="Logo Copropiedad"
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <div className="text-center text-slate-400">
                      <ImageIcon className="h-6 w-6 mx-auto mb-1 opacity-40" />
                      <span className="text-[9px] block leading-tight">Sin logo</span>
                    </div>
                  )}
                </div>

                {/* Controles de carga */}
                <div className="space-y-1.5 flex-1 w-full">
                  <input
                    type="file"
                    ref={logoFileInputRef}
                    onChange={handleLogoFileChange}
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    className="hidden"
                  />

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={isUploadingLogo}
                      onClick={() => logoFileInputRef.current?.click()}
                      className="text-xs h-8 gap-1.5 border-emerald-500/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950 font-semibold"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {isUploadingLogo ? 'Subiendo a Supabase...' : adminContactSettings.copropiedad_logo_url ? 'Cambiar Logo' : 'Subir Logo de la PH'}
                    </Button>

                    {(adminContactSettings.copropiedad_logo_data || adminContactSettings.copropiedad_logo_url) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={handleRemoveLogo}
                        className="text-xs h-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950 gap-1"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Eliminar
                      </Button>
                    )}
                  </div>

                  <p className="text-[10px] text-slate-500 dark:text-slate-400">
                    Formatos: PNG, JPG, WebP o SVG (máx. 5 MB). Carpeta en Supabase:{' '}
                    <strong className="font-mono text-emerald-700 dark:text-emerald-400">
                      ph-logos/{PhLogoStorageService.getPhFolderName(adminContactSettings.copropiedad_nombre)}/
                    </strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Administrador y WhatsApp */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Nombre del Administrador(a) *</Label>
                <Input
                  value={adminContactSettings.administrador_nombre}
                  onChange={(e) => setAdminContactSettings({ ...adminContactSettings, administrador_nombre: e.target.value })}
                  placeholder="Ej: Dra. Claudia Patricia Gómez"
                  className="text-xs h-8"
                />
              </div>

              <div className="space-y-1 p-2.5 rounded-lg border bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-bold text-emerald-900 dark:text-emerald-300 flex items-center gap-1">
                    <MessageCircle className="h-3.5 w-3.5 text-emerald-600" />
                    WhatsApp de Atención *
                  </Label>
                  <span className="text-[10px] text-emerald-700 dark:text-emerald-400 font-semibold">Botón del Portal</span>
                </div>
                <Input
                  value={adminContactSettings.administrador_whatsapp}
                  onChange={(e) => setAdminContactSettings({ ...adminContactSettings, administrador_whatsapp: e.target.value })}
                  placeholder="Ej: 3184567890"
                  className="text-xs h-8 font-mono bg-white dark:bg-slate-900 border-emerald-300"
                />
                <span className="text-[10px] text-muted-foreground block mt-0.5">
                  Número al que se abrirá el chat de WhatsApp con el deudor.
                </span>
              </div>
            </div>

            {/* Teléfono PBX y Correo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5 text-blue-600" />
                  Teléfono Fijo / PBX
                </Label>
                <Input
                  value={adminContactSettings.administrador_telefono}
                  onChange={(e) => setAdminContactSettings({ ...adminContactSettings, administrador_telefono: e.target.value })}
                  placeholder="Ej: (601) 745-8900 Ext. 102"
                  className="text-xs h-8 font-mono"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5 text-indigo-600" />
                  Correo de Cartera / Administración
                </Label>
                <Input
                  type="email"
                  value={adminContactSettings.administrador_email}
                  onChange={(e) => setAdminContactSettings({ ...adminContactSettings, administrador_email: e.target.value })}
                  placeholder="cartera@torresdelparque.com"
                  className="text-xs h-8"
                />
              </div>
            </div>

            {/* Horario y Oficina */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-amber-600" />
                  Horario de Atención
                </Label>
                <Input
                  value={adminContactSettings.administrador_horario}
                  onChange={(e) => setAdminContactSettings({ ...adminContactSettings, administrador_horario: e.target.value })}
                  placeholder="Ej: Lun - Vie 8:00 AM - 5:00 PM"
                  className="text-xs h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 text-rose-600" />
                  Ubicación de la Oficina
                </Label>
                <Input
                  value={adminContactSettings.administrador_oficina}
                  onChange={(e) => setAdminContactSettings({ ...adminContactSettings, administrador_oficina: e.target.value })}
                  placeholder="Ej: Oficina de Administración - Torre 1, Piso 1"
                  className="text-xs h-8"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAdminContactModalOpen(false)}
              className="text-xs h-8"
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={isSavingAdminContact}
              onClick={handleSaveAdminContactSettings}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 font-semibold gap-1.5 shadow-sm"
            >
              <Check className="h-3.5 w-3.5" />
              {isSavingAdminContact ? 'Guardando...' : 'Guardar y Sincronizar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default PaymentAgreementsTab;
